import React, { useState, useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Badge, Typography } from '@telavita-core/react-design-kit'

import MedicalRecordsService from 'services/medicalRecords'

import { SearchMultiSelect } from '../SearchMultiSelect'
import { OtherOptionInput } from '../OtherOptionInput'

import { MedicalRecordFormData } from '../../medicalRecordSchema'
import { Option, OTHER_SYMPTOM_OPTION_ID } from '../../types/option'

import * as S from './styles'

const OTHERS_LABEL = 'Outros(as)'

interface SymptomsOption extends Option {
  id: number
  description: string
}

export function SymptomsSelect(): JSX.Element {
  const [symptomsOptions, setSymptomsOptions] = useState([])

  const {
    control,
    watch,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext<MedicalRecordFormData>()
  const symptomsSelected = watch('symptoms') || []

  const hasSelectedOptions = symptomsSelected.length > 0
  const hasOtherOptionInSelectedOptions = symptomsSelected.some(
    (op) => op.description === OTHERS_LABEL
  )

  const handleChangeValue = (value: SymptomsOption[]) =>
    setValue('symptoms', value)

  function handleRemoveSelectedOption(optionId: number) {
    const options = symptomsSelected.filter((op) => op.id !== optionId)
    if (optionId === OTHER_SYMPTOM_OPTION_ID) {
      resetField('other_symptom')
    }
    handleChangeValue(options)
  }

  const getSymptoms = useCallback(
    async (query: string, page: number, isSearch = false) => {
      const result = await MedicalRecordsService.listSymptoms(query, page)
      const options = result.symptoms.map((symptom) => {
        return {
          ...symptom,
          id: symptom.id,
          name: symptom.description,
        }
      })

      setSymptomsOptions((state) => {
        if (isSearch) return options

        return [...state, ...options]
      })

      return result
    },
    []
  )

  return (
    <S.SymptomsSelectContainer>
      <Controller
        control={control}
        name='symptoms'
        render={({ field }) => (
          <SearchMultiSelect
            {...field}
            selectedOptions={symptomsSelected}
            onChange={field.onChange}
            options={symptomsOptions}
            label='Sintomas *'
            description='Se não houver, digite e/ou selecione “nenhum(a)” para adicionar. Caso as opções não atendam, escolha "outros(as)".'
            floatingLabel='Digite e/ou selecione para adicionar'
            inputPlaceholder='Digite e/ou selecione para adicionar'
            emptyOptionsMessage='Nenhum resultado. Verifique sua digitação.'
            errorMessage={errors.symptoms?.message}
            loadOptions={getSymptoms}
          />
        )}
      />
      {hasSelectedOptions && (
        <S.SelectedOptionsContainer>
          {symptomsSelected.map((option) => (
            <Badge
              key={option.id}
              border
              rounded
              colorScheme='white'
              icon='Close'
              variant='lg'
              onClickIcon={() =>
                handleRemoveSelectedOption(option.id as number)
              }
            >
              <Typography color='primary' variant='content2'>
                {option.name}
              </Typography>
            </Badge>
          ))}
        </S.SelectedOptionsContainer>
      )}
      {hasOtherOptionInSelectedOptions && (
        <Controller
          control={control}
          name='other_symptom'
          render={({ field, fieldState: { error } }) => (
            <OtherOptionInput
              {...field}
              errorMessage={error?.message}
              placeholder='Qual(is)?'
            />
          )}
        />
      )}
    </S.SymptomsSelectContainer>
  )
}
